import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Patient } from "@models/patient";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { ListResponse } from "@models/list-response";
import buildFormData from "@utils/buildFormData";
import { personApi } from "@store/personApi";
import { accountSetupApi } from "@store/accountSetupApi";
import { characterApi } from "@store/characterApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export const patientApi = createApi({
  reducerPath: "patientApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/patients`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Patient", "FamilyMember"],
  endpoints: (builder) => ({
    createPatient: builder.mutation<Patient, Partial<Patient>>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: ({ avatar, ...rest }: Partial<Patient>) => {
        const formData = new FormData();
        buildFormData(formData, rest);
        if (avatar) {
          formData.append("avatar", avatar);
        }
        return {
          url: "",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
      },
    }),
    updatePatient: builder.mutation<Patient, Partial<Patient>>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: ({ id, avatar, ...patient }: Partial<Patient>) => {
        const formData = new FormData();
        buildFormData(formData, patient);
        if (avatar) {
          formData.append("avatar", avatar);
        }

        return {
          url: id!,
          method: "PATCH",
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
        dispatch(characterApi.util.invalidateTags(["Character"]));
      },
    }),
    getPatients: builder.query<ListResponse<Patient>, void>({
      providesTags: ["Patient", "FamilyMember"],
      query: () => "",
    }),
    getPatient: builder.query<Patient, string>({
      providesTags: ["Patient", "FamilyMember"],
      query: (id: string) => id,
    }),
  }),
});

export const {
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useGetPatientsQuery,
  useGetPatientQuery,
} = patientApi;
